import XIcon from "@mui/icons-material/Close"
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"

import { DialogProps } from "@/dialogs"
import { Attraction, AttractionType } from "@/common/types"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import ControlledTextInput from "@/components/controlledInputs/ControlledTextInput"
import GoogleAutoAddress from "@/components/controlledInputs/ControlledGoogleAddress"
import useListAttractionTypes from "@/hooks/api/attractions/useListAttractionTypes"
import ControlledCheckboxGroup from "@/components/controlledInputs/ControlledCheckBoxGroup"
import useEditAttraction from "@/hooks/api/attractions/useEditAttraction"
import { useEffect } from "react"

const editAttractionFormData = z.object({
  attraction_id: z.number().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  attraction_types: z.array(z.number()).optional(),
  address: z.string().optional(),
  location: z.string().optional(),
  position: z
    .object({
      lat: z.number(),
      long: z.number(),
    })
    .optional(),
  price: z.string().optional(),
  actionUrl: z.string().optional(),
})

export type EditAttractionFormDataType = z.infer<typeof editAttractionFormData>

interface EditAttractionDialogProps extends DialogProps {
  attraction: Attraction
}

function EditAttractionDialog({
  isOpen,
  closeDialog,
  attraction,
}: EditAttractionDialogProps) {
  function handleClose() {
    closeDialog()
  }
  const { data: attractionTypes, isLoading: attractionTypesLoading } =
    useListAttractionTypes()
  const { mutateAsync: editAttraction } = useEditAttraction(
    attraction.attraction_id
  )

  const defaultValues: EditAttractionFormDataType = {
    title: attraction.title,
    description: attraction.description,
    attraction_types: attraction.attraction_types
      ? attraction.attraction_types.map(
          (attractionType) => attractionType.attraction_type_id
        )
      : [],
    address: attraction.address,
    location: attraction.location,
    position: { lat: attraction.position.x, long: attraction.position.y },
    price: attraction.price,
    actionUrl: attraction.action_url,
  }

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<EditAttractionFormDataType>({
    resolver: zodResolver(editAttractionFormData),
    defaultValues: defaultValues,
  })

  const handleSetValue = (value: string) => {
    setValue("address", value)
  }

  const submitAttraction = async (data: EditAttractionFormDataType) => {
    const geocoder = new window.google.maps.Geocoder()
    await geocoder.geocode({ address: data.address }, (results, status) => {
      if (status === "OK" && results) {
        console.log(results[0].geometry.location)
        const lat = results[0].geometry.location.lat()
        const lng = results[0].geometry.location.lng()
        data["position"] = { lat: lat, long: lng }
      } else {
        console.error("Geocoding failed:", status)
      }
    })
    await editAttraction(data).then((res) => {
      console.log(res)
      closeDialog()
    })
  }

  useEffect(() => {
    console.log("errors", errors)
  }, [errors])

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        minWidth={"500px"}
        pl={3}
        pr={1}
        py={1}
        sx={{
          backgroundColor: "primary.main",
          color: "primary.contrastText",
        }}
      >
        <Typography>Edit Attraction</Typography>
        <Box>
          <IconButton color="inherit" onClick={handleClose}>
            <XIcon />
          </IconButton>
        </Box>
      </Stack>
      <DialogContent
        sx={{
          py: 2,
        }}
      >
        <form onSubmit={handleSubmit(submitAttraction)}>
          {!attractionTypesLoading && attractionTypes && (
            <Stack spacing={1}>
              <ControlledTextInput
                variant="outlined"
                control={control}
                name="title"
                label="Attraction Title"
                type="text"
              />
              <ControlledTextInput
                control={control}
                name="description"
                label="Attraction Description"
                type="text"
              />
              <ControlledTextInput
                control={control}
                name="location"
                label="Location Title"
                type="text"
              />
              <GoogleAutoAddress
                control={control}
                name="address"
                label="Attraction Address"
                type="text"
                setValue={handleSetValue}
              />
              <ControlledTextInput
                control={control}
                name="price"
                label="Price"
                type="text"
              />
              <ControlledCheckboxGroup
                control={control}
                name="attraction_types"
                label="Attraction Type"
                options={attractionTypes.data.map(
                  (element: AttractionType) => {
                    return {
                      label: element.human_name,
                      value: element.attraction_type_id,
                    }
                  }
                )}
              />
              <ControlledTextInput
                control={control}
                name="actionUrl"
                label="Action Url"
              />
            </Stack>
          )}
          <Stack direction="row" justifyContent="flex-end" spacing={2} mt={5}>
            <Button variant="text" color="inherit" onClick={closeDialog}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" type="submit">
              Save
            </Button>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default EditAttractionDialog
